import {useMemo} from 'react';
import {doc, cancelCircleFilled} from '@sail/icons/react/Icon';
import {Icon, Tooltip, Spinner, view, tokens} from '@sail/ui';
import {animate, view as animatedView, AnimatePresence} from '@sail/engine';
import {useIntl} from 'react-intl';
import type {StagedFile, FileError} from './useFileStaging';
import {
  supportedImageMimeTypes,
  errorCodeToMessage,
  fileStagingMessages,
} from './useFileStaging';

const THUMBNAIL_SIZE = '64px';
const MAX_TOOLTIP_NAME_LENGTH = 20;

function truncateMiddle(name: string, maxLength: number): string {
  if (name.length <= maxLength) return name;
  const endChars = Math.ceil((maxLength - 3) / 2);
  const startChars = maxLength - 3 - endChars;
  return `${name.slice(0, startChars)}...${name.slice(name.length - endChars)}`;
}

function useObjectUrl(file: File): string | undefined {
  return useMemo(() => {
    if (!supportedImageMimeTypes.includes(file.type)) return undefined;
    return URL.createObjectURL(file);
  }, [file]);
}

function ThumbnailPreview({
  file,
  isUploading,
}: {
  file: File;
  isUploading: boolean;
}) {
  const objectUrl = useObjectUrl(file);

  if (objectUrl) {
    return (
      <view.img
        src={objectUrl}
        alt={file.name}
        data-testid="staged-file-thumbnail"
        css={{
          width: 'fill',
          height: 'fill',
          objectFit: 'cover',
          opacity: isUploading ? 0.5 : 1,
        }}
      />
    );
  }

  return (
    <view.div
      data-testid="staged-file-doc-icon"
      css={{
        width: 'fill',
        height: 'fill',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: isUploading ? 0.5 : 1,
      }}
    >
      <Icon icon={doc} size="small" />
    </view.div>
  );
}

function RemoveButton({
  fileName,
  onRemove,
}: {
  fileName: string;
  onRemove: () => void;
}) {
  return (
    <view.button
      type="button"
      aria-label={`Remove ${fileName}`}
      onClick={onRemove}
      data-testid="staged-file-remove"
      css={{
        position: 'absolute',
        top: '-6px',
        right: '-6px',
        width: '18px',
        height: '18px',
        borderRadius: 'round',
        border: 'none',
        background: 'none',
        color: 'icon.action.destructive',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        '.staged-file-chip:hover &': {
          display: 'flex',
        },
      }}
    >
      <Icon icon={cancelCircleFilled} size="small" />
    </view.button>
  );
}

export function StagedFileChip({
  stagedFile,
  onRemove,
}: {
  stagedFile: StagedFile;
  onRemove: () => void;
}) {
  const isUploading = stagedFile.status === 'uploading';

  return (
    <Tooltip
      type="label"
      trigger={
        <view.div
          className="staged-file-chip"
          css={{
            position: 'relative',
            width: THUMBNAIL_SIZE,
            height: THUMBNAIL_SIZE,
            borderRadius: 'small',
            overflow: 'visible',
            border: '1px solid',
            borderColor: 'border',
            flexShrink: 0,
          }}
        >
          <view.div
            css={{
              width: 'fill',
              height: 'fill',
              borderRadius: 'small',
              overflow: 'hidden',
            }}
          >
            <ThumbnailPreview
              file={stagedFile.file}
              isUploading={isUploading}
            />
          </view.div>
          {isUploading ? (
            <view.div
              css={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spinner size="small" />
            </view.div>
          ) : null}
          <RemoveButton fileName={stagedFile.file.name} onRemove={onRemove} />
        </view.div>
      }
    >
      <view.span
        css={{
          font: 'body.small',
          display: 'inline-block',
          textAlign: 'center',
          verticalAlign: 'middle',
        }}
      >
        {truncateMiddle(stagedFile.file.name, MAX_TOOLTIP_NAME_LENGTH)}
      </view.span>
    </Tooltip>
  );
}

export function StagedFileList({
  stagedFiles,
  onRemoveStagedFile,
}: {
  stagedFiles: StagedFile[];
  onRemoveStagedFile: (index: number) => void;
}) {
  return (
    <view.div
      css={{
        overflow: 'hidden',
        transition: 'max-height 200ms ease, opacity 200ms ease',
        maxHeight: stagedFiles.length > 0 ? '200px' : '0px',
        opacity: stagedFiles.length > 0 ? 1 : 0,
      }}
    >
      {stagedFiles.length > 0 ? (
        <view.div
          data-testid="staged-files-row"
          css={{
            display: 'flex',
            gap: 'small',
            paddingX: 'medium',
            paddingTop: 'small',
          }}
        >
          {stagedFiles.map((sf, i) => (
            <StagedFileChip
              key={`${sf.file.name}-${sf.file.size}`}
              stagedFile={sf}
              onRemove={() => onRemoveStagedFile(i)}
            />
          ))}
        </view.div>
      ) : null}
    </view.div>
  );
}

const FADE_DURATION = 300;

export function UploadErrorOverlay({
  uploadErrors,
}: {
  uploadErrors?: FileError[];
}) {
  const intl = useIntl();
  const hasErrors = !!uploadErrors && uploadErrors.length > 0;

  return (
    <AnimatePresence>
      {hasErrors ? (
        <animatedView.div
          data-testid="upload-errors"
          uses={[
            animate.out({
              duration: FADE_DURATION,
              opacity: 0,
              easing: 'ease-in',
            }),
          ]}
          css={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            paddingX: 'medium',
            paddingBottom: 'xxsmall',
            stack: 'y',
            gap: 'none',
          }}
        >
          {uploadErrors.map((error) => {
            const messageDescriptor =
              errorCodeToMessage[error.code] ??
              fileStagingMessages.unexpectedError;

            return (
              <view.p
                key={error.code}
                css={{
                  font: 'body.small',
                  fontWeight: 'regular',
                  color:
                    tokens.color.text.feedback.attention.subdued.toString(),
                  textAlign: 'center',
                  margin: 0,
                  paddingBottom: 'xxsmall',
                  gap: 'none',
                }}
              >
                {intl.formatMessage(messageDescriptor)}
              </view.p>
            );
          })}
        </animatedView.div>
      ) : null}
    </AnimatePresence>
  );
}
